import { useState, useEffect } from 'react';
import { PreloadDataFetchEnum } from '@am3nd-org/pointcraft-types';
import type { PreloadDataFetchType, PartialPreloadDataType } from '@am3nd-org/pointcraft-types';

import { paths } from 'src/routes/paths';
import { useRouter } from 'src/routes/hooks';

import { useFetchAllData } from 'src/utils/guards/preload-data-utils';
import { getUsesBleReader, getUserDataCache, saveUserDataCache } from 'src/utils/Services/DB_Utils';

import useUserDataStore from 'src/store/user';
import { useSystemStore } from 'src/store/SystemStore';
import { useSettingStore } from 'src/online-payment/stores';
import type { CurrentTerminal } from 'src/store/SystemStore/types';
import useAuthStore, { needsReAuthentication } from 'src/auth/store/firebase/auth-store';

import { SplashScreen } from 'src/components/loading-screen';

/**
 * Helper to determine if an error is authentication-related
 */
function isAuthError(error: Error | null): boolean {
  if (!error) return false;

  const errorMessage = error.message?.toLowerCase() || '';
  const errorString = String(error).toLowerCase();

  // Check for common auth error patterns
  const authErrorPatterns = [
    '401',
    'unauthorized',
    'unauthenticated',
    'auth',
    'token',
    'permission denied',
    'permission-denied',
    'not authenticated',
    'no user found',
    'no auth token',
    'failed to get auth token',
  ];

  return authErrorPatterns.some(
    (pattern) => errorMessage.includes(pattern) || errorString.includes(pattern)
  );
}

// ----------------------------------------------------------------------

type Props = {
  children: React.ReactNode;
  type: PreloadDataFetchEnum[];
};

export default function PreloadDataGuard({ children, type = [PreloadDataFetchEnum.ALL] }: Props) {
  // null = still reading IndexedDB; resolved once cache check completes
  const [resolvedTypes, setResolvedTypes] = useState<PreloadDataFetchType[] | null>(null);
  // true = cache was found; children can render without waiting for the network response
  const [hasCachedData, setHasCachedData] = useState(false);

  const { isLoading, data, error } = useFetchAllData({ type: resolvedTypes });
  const { setUserData, setBusinessData } = useUserDataStore();
  const {
    currentDevice,
    setDevices,
    setTerminals,
    setUsesBleReader,
    setCurrentTerminal,
    setExecutionMode,
  } = useSystemStore();
  const { setPlans } = useSettingStore();

  const router = useRouter();

  // Debug logging for preload state
  useEffect(() => {
    console.log('[PreloadDataGuard] State:', {
      isLoading,
      hasData: !!data,
      hasError: !!error,
      errorMessage: error?.message || null,
    });
  }, [isLoading, data, error]);

  // Check cache on mount. If found, hydrate the store immediately and skip USER_DATA
  // from the network request — the Firestore listener keeps it fresh via version sync.
  useEffect(() => {
    getUserDataCache()
      .then((cached) => {
        if (cached) {
          setUserData(cached);
          setBusinessData(cached.business);
          setHasCachedData(true);
          setResolvedTypes([
            PreloadDataFetchEnum.DEVICES,
            PreloadDataFetchEnum.TERMINALS,
            PreloadDataFetchEnum.RECURRING_PLANS,
          ]);
        } else {
          setResolvedTypes(type);
        }
      })
      .catch(() => {
        // On DB read error fall back to the full fetch
        setResolvedTypes(type);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    console.log('[PreloadDataGuard] Effect running with data:', data);
    if (!data) return;

    const partial = data as PartialPreloadDataType;

    if (partial.userData) {
      console.log('[PreloadDataGuard] Setting user data:', partial.userData);
      setUserData(partial.userData);
      setBusinessData(partial.userData.business);
      saveUserDataCache(partial.userData).catch((err) => {
        console.error('[PreloadDataGuard] Failed to cache userData:', err);
      });
    }

    if (partial.devices) setDevices(partial.devices);
    if (partial.terminals) setTerminals(partial.terminals);
    if (partial.recurringPlans) setPlans(partial.recurringPlans);
  }, [data, setUserData, setBusinessData, setDevices, setTerminals, setPlans]);

  const { logout } = useAuthStore();

  useEffect(() => {
    const handleError = async () => {
      if (!error) return;

      console.error('[PreloadDataGuard] Error in preload data:', error);

      // Check if this is an authentication error
      if (isAuthError(error) || needsReAuthentication()) {
        console.warn('[PreloadDataGuard] ⚠️ Authentication error detected - redirecting to login');
        console.warn('[PreloadDataGuard] Error details:', {
          message: error.message,
          needsReAuth: needsReAuthentication(),
        });

        // Clear auth state and redirect to login
        try {
          await logout();
        } catch (logoutError) {
          console.error('[PreloadDataGuard] Error during logout:', logoutError);
        }

        router.replace(paths.auth.login);
        return;
      }

      // For non-auth errors, redirect to contact support
      console.error('[PreloadDataGuard] Non-auth error - redirecting to contact support');
      router.replace(paths.system.contactSupport);
    };

    handleError();
  }, [error, router, logout]);

  useEffect(() => {
    const saveData = async () => {
      try {
        const usesBleReader = await getUsesBleReader();
        setUsesBleReader(usesBleReader?.data?.usesBleReader ?? false);
      } catch (e) {
        console.error('Error setting usesBleReader: AZ', e);
      }
    };
    saveData();
  }, [data, setUsesBleReader]);

  useEffect(() => {
    const setupCurrentTerminal = async () => {
      const partial = data as PartialPreloadDataType | null;
      if (!partial?.devices) return;
      if (!currentDevice?.id) return;

      try {
        const currentDeviceInfo = partial.devices.find((d) => d.id === currentDevice.id);
        const terminal = currentDeviceInfo?.terminal as CurrentTerminal | undefined;

        if (terminal) {
          setCurrentTerminal(terminal);
          setExecutionMode(terminal.executionMode);
        }
      } catch (setupError) {
        console.error('Error setting up current terminal:', setupError);
        // Don't throw - this is non-critical
      }
    };

    setupCurrentTerminal();
  }, [data, currentDevice, setCurrentTerminal, setExecutionMode]);

  // Still reading IndexedDB — don't render or fire any request yet
  if (resolvedTypes === null) return <SplashScreen />;

  if (error) return <SplashScreen />;

  // Cache hit: show children immediately; devices/terminals finish loading in the background.
  // No cache: must wait for the full network response (includes USER_DATA).
  const isStillLoading = !hasCachedData && isLoading;
  return isStillLoading ? <SplashScreen /> : <>{children}</>;
}
